/**
 * "Live Traces" tab — polished list view (PR5a).
 *
 * Spec: dev/projects/sl2-sstv2-live-trace.md §4.1
 *
 * Filters: time range (last 1h default), tenant dropdown, event-type dropdown,
 * reservation id text, locker id text, status chips. Cursor-based pagination,
 * page size 50. Polls every 10s while any visible trace is in-progress.
 *
 * NOTE on filter shape: the RTK Query slice (sstv2TraceApi.ts) currently only
 * forwards tenantId / eventType / reservationId / limit / cursor to the
 * backend. Locker, status, and time-range are applied client-side as a
 * post-filter on the accumulated page so we don't block the UI on backend
 * widening. (The backend can extend this later — see §4.4.)
 */
import {
  Check,
  Close,
  Link as LinkIcon,
  Refresh,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import { useGetSstv2EventDiagramsQuery } from "../../features/partners/partnersApi"
import { useGetTenantsQuery } from "../../features/things/thingsSaApi"
import { fmtAbsolute, fmtDuration, fmtRelative } from "./formatters"
import { SstSl2IdPair } from "./SstSl2IdPair"
import {
  Sstv2TraceMetricsSection,
  type TraceMetricFilterTarget,
} from "./Sstv2TraceMetricsSection"
import { useGetTraceListQuery, type GetTraceListArgs } from "./sstv2TraceApi"
import { getMuiTraceStatusColor } from "./traceColors"
import type { TraceItem, TraceStatus } from "./types"

const PAGE_SIZE = 50
const POLL_INTERVAL_MS = 10_000

const DEFAULT_TIME_RANGE_MS = 60 * 60 * 1000 // 1h

/**
 * URL query-param keys. The trace tab is URL-first: filter state is
 * serialized into the query string so the page is bookmarkable and
 * shareable. Backend filters (tenant/eventType/reservation) only write to
 * the URL on Apply — typing into the input doesn't pollute the share link.
 * Client-side filters (locker/range/status) write on every change.
 */
const URL_KEYS = {
  tenant: "tenant",
  eventType: "eventType",
  reservation: "reservation",
  /** One V1→V2 migration attempt (partner `sl2_locker_id`) — a backend filter. */
  migration: "migrationId",
  locker: "locker",
  range: "range",
  status: "status",
} as const

const ALL_STATUSES: TraceStatus[] = [
  "succeeded",
  "failed",
  "partial",
  "in-progress",
  "discarded",
]

const TIME_RANGE_OPTIONS = [
  { label: "Last 15 minutes", short: "15m", ms: 15 * 60 * 1000 },
  { label: "Last 1 hour", short: "1h", ms: 60 * 60 * 1000 },
  { label: "Last 6 hours", short: "6h", ms: 6 * 60 * 60 * 1000 },
  { label: "Last 24 hours", short: "24h", ms: 24 * 60 * 60 * 1000 },
  { label: "Last 7 days", short: "7d", ms: 7 * 24 * 60 * 60 * 1000 },
  { label: "All time", short: "All", ms: Number.POSITIVE_INFINITY },
]

/**
 * Structural compare of the args that drive the trace-list query. Used to
 * make Apply / Reset a no-op when nothing actually changed, so we don't
 * tear down accumulator + cursor for a redundant click.
 */
const sameAppliedArgs = (a: GetTraceListArgs, b: GetTraceListArgs): boolean =>
  a.tenantId === b.tenantId &&
  a.eventType === b.eventType &&
  a.reservationId === b.reservationId &&
  a.migrationId === b.migrationId &&
  a.limit === b.limit &&
  a.cursor === b.cursor

const StatusChip: FC<{ status: TraceStatus; size?: "small" | "medium" }> = ({
  status,
  size = "small",
}) => <Chip size={size} label={status} color={getMuiTraceStatusColor(status)} />

export const Sstv2TracesTab: FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // ---------------------------------------------------------------------
  // Filter state — hydrated ONCE from the URL on mount via lazy useState
  // initializers, then owned by the component. Writes back to the URL via
  // `updateUrl` whenever filters change (see effect + handlers below).
  // ---------------------------------------------------------------------
  const [tenantId, setTenantId] = useState(
    () => searchParams.get(URL_KEYS.tenant) ?? "",
  )
  const [eventType, setEventType] = useState(
    () => searchParams.get(URL_KEYS.eventType) ?? "",
  )
  const [reservationId, setReservationId] = useState(
    () => searchParams.get(URL_KEYS.reservation) ?? "",
  )
  const [migrationId, setMigrationId] = useState(
    () => searchParams.get(URL_KEYS.migration) ?? "",
  )
  const [lockerId, setLockerId] = useState(
    () => searchParams.get(URL_KEYS.locker) ?? "",
  )
  const [timeRangeMs, setTimeRangeMs] = useState<number>(() => {
    const raw = searchParams.get(URL_KEYS.range)
    if (!raw) return DEFAULT_TIME_RANGE_MS
    const n = Number(raw)
    return Number.isFinite(n) ? n : DEFAULT_TIME_RANGE_MS
  })
  const [enabledStatuses, setEnabledStatuses] = useState<Set<TraceStatus>>(
    () => {
      const raw = searchParams.get(URL_KEYS.status)
      if (!raw) return new Set(ALL_STATUSES)
      const parsed = raw
        .split(",")
        .filter((s): s is TraceStatus =>
          (ALL_STATUSES as readonly string[]).includes(s),
        )
      return parsed.length > 0 ? new Set(parsed) : new Set(ALL_STATUSES)
    },
  )

  // Applied filters (the ones that actually drive the query). Decoupled so
  // the user can edit text inputs without spamming requests. Initialized
  // from URL so a bookmarked link auto-applies the backend filters.
  const [appliedArgs, setAppliedArgs] = useState<GetTraceListArgs>(() => ({
    tenantId: searchParams.get(URL_KEYS.tenant) || undefined,
    eventType: searchParams.get(URL_KEYS.eventType) || undefined,
    reservationId: searchParams.get(URL_KEYS.reservation) || undefined,
    // Deep-linkable: the migration page links straight here with ?migrationId=…
    migrationId: searchParams.get(URL_KEYS.migration) || undefined,
    limit: PAGE_SIZE,
  }))

  // Centralized URL writer. Always uses `replace: true` so each filter
  // tweak doesn't pile a new history entry — the back button still
  // navigates to the previous page, not to the previous filter state.
  const updateUrl = useCallback(
    (mutator: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          mutator(next)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  // Sync client-side filters → URL on every change. These don't have a
  // separate Apply step (they filter the already-loaded data), so the URL
  // mirrors the live state.
  //
  // The `skipFirstSyncRef` guard prevents the initial render from writing
  // the just-hydrated values back to the URL — that would be a redundant
  // setSearchParams call before any user interaction.
  const skipFirstSyncRef = useRef(true)
  useEffect(() => {
    if (skipFirstSyncRef.current) {
      skipFirstSyncRef.current = false
      return
    }
    updateUrl((p) => {
      if (lockerId) p.set(URL_KEYS.locker, lockerId)
      else p.delete(URL_KEYS.locker)

      if (timeRangeMs !== DEFAULT_TIME_RANGE_MS) {
        p.set(URL_KEYS.range, String(timeRangeMs))
      } else {
        p.delete(URL_KEYS.range)
      }

      const allStatusesEnabled = enabledStatuses.size === ALL_STATUSES.length
      if (allStatusesEnabled) {
        p.delete(URL_KEYS.status)
      } else {
        p.set(URL_KEYS.status, Array.from(enabledStatuses).join(","))
      }
    })
  }, [lockerId, timeRangeMs, enabledStatuses, updateUrl])

  const [accumulated, setAccumulated] = useState<TraceItem[]>([])
  const [cursor, setCursor] = useState<string | undefined>()

  // Tenant cache -----------------------------------------------------------
  const tenantsQuery = useGetTenantsQuery({})
  const tenants = useMemo(
    () =>
      (tenantsQuery.data?.things ?? []) as Array<{ id: string; name?: string }>,
    [tenantsQuery.data],
  )
  const tenantNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of tenants) {
      if (t.id) m.set(t.id, t.name ?? t.id)
    }
    return m
  }, [tenants])

  // Event-type catalog. Sourced from the same backend registry that drives
  // the per-event flow-diagrams tab, so the dropdown stays in sync with
  // whatever event names are actually persisted on trace headers (e.g.
  // "reservation:pickedUp", "locker:updated"). Hand-maintained lists drift.
  const eventDiagramsQuery = useGetSstv2EventDiagramsQuery()
  const eventTypeGroups = useMemo(() => {
    const items = eventDiagramsQuery.data?.data ?? []
    const buckets: Record<
      string,
      Array<{ eventType: string; label: string }>
    > = {}
    for (const it of items) {
      const cat = it.category ?? "other"
      if (!buckets[cat]) buckets[cat] = []
      buckets[cat].push({
        eventType: it.eventType,
        label: it.label || it.eventType,
      })
    }
    for (const cat of Object.keys(buckets)) {
      buckets[cat].sort((a, b) => a.eventType.localeCompare(b.eventType))
    }
    // Stable display order — `reservation` first since it's the dominant
    // axis, then `locker`, `door`, and any future categories trail.
    const preferred = ["reservation", "locker", "door"]
    const ordered = [
      ...preferred.filter((c) => buckets[c]),
      ...Object.keys(buckets)
        .filter((c) => !preferred.includes(c))
        .sort(),
    ]
    return ordered.map((category) => ({
      category,
      items: buckets[category],
    }))
  }, [eventDiagramsQuery.data])

  // Determine if any visible trace is in-progress → enable polling.
  const anyInProgress = useMemo(
    () => accumulated.some((t) => t.status === "in-progress"),
    [accumulated],
  )

  const { data, isFetching, refetch } = useGetTraceListQuery(
    { ...appliedArgs, cursor },
    {
      pollingInterval: anyInProgress ? POLL_INTERVAL_MS : 0,
    },
  )

  // Reset accumulator when the *applied* filters change. This effect ONLY
  // fires when `appliedArgs` content actually changed — `onApplyFilters` and
  // `onReset` use a structural-equality guard so clicking Apply with no
  // input changes is a true no-op (otherwise we'd reset cursor + clear
  // pagination state for nothing).
  useEffect(() => {
    setAccumulated([])
    setCursor(undefined)
  }, [appliedArgs])

  // Append/merge new page when results come in.
  useEffect(() => {
    if (!data?.data?.items) return
    const sortDesc = (a: TraceItem, b: TraceItem) =>
      a.startedAt < b.startedAt ? 1 : a.startedAt > b.startedAt ? -1 : 0
    setAccumulated((prev) => {
      if (cursor == null) {
        // Page 1 (initial load, polling, refresh) — UNION with prev so
        // polling-discovered rows aren't lost. After a filter change the
        // reset effect above leaves prev=[], so the union devolves to the
        // fresh page items, which is exactly what we want.
        const map = new Map<string, TraceItem>()
        for (const it of data.data.items) map.set(it.traceId, it)
        for (const it of prev) {
          if (!map.has(it.traceId)) map.set(it.traceId, it)
        }
        return Array.from(map.values()).sort(sortDesc)
      }
      // Pagination: append, dedupe.
      const seen = new Set(prev.map((i) => i.traceId))
      const fresh = data.data.items.filter((i) => !seen.has(i.traceId))
      return [...prev, ...fresh]
    })
  }, [data, cursor])

  // Client-side filtering for axes the backend doesn't yet handle. Final
  // step always re-sorts newest-first — the pagination append path doesn't
  // sort, and the Scan-based fallback doesn't preserve cross-page order, so
  // we can't trust `accumulated` to already be in order.
  const visible = useMemo(() => {
    const cutoff =
      Number.isFinite(timeRangeMs) && timeRangeMs > 0
        ? Date.now() - timeRangeMs
        : 0
    const lockerNeedle = lockerId.trim().toLowerCase()
    const filtered = accumulated.filter((t) => {
      if (!enabledStatuses.has(t.status)) return false
      if (cutoff > 0) {
        const ts = new Date(t.startedAt).getTime()
        if (Number.isFinite(ts) && ts < cutoff) return false
      }
      if (lockerNeedle) {
        if (!(t.lockerId ?? "").toLowerCase().includes(lockerNeedle))
          return false
      }
      return true
    })
    return filtered.sort((a, b) =>
      a.startedAt < b.startedAt ? 1 : a.startedAt > b.startedAt ? -1 : 0,
    )
  }, [accumulated, enabledStatuses, timeRangeMs, lockerId])

  const onApplyFilters = useCallback(() => {
    const tenant = tenantId.trim()
    const evt = eventType.trim()
    const reservation = reservationId.trim()
    const migration = migrationId.trim()
    const next: GetTraceListArgs = {
      tenantId: tenant || undefined,
      eventType: evt || undefined,
      reservationId: reservation || undefined,
      migrationId: migration || undefined,
      limit: PAGE_SIZE,
    }
    // Only commit if filter content actually changed. Without this guard,
    // clicking Apply with unchanged inputs creates a new object reference,
    // which retriggers the reset effect (clearing accumulator + cursor) and
    // collapses pagination state — visible to the user as rows disappearing.
    setAppliedArgs((prev) => (sameAppliedArgs(prev, next) ? prev : next))
    // Backend filters are committed to the URL only on Apply, never on
    // keystroke — so a half-typed reservation ID never ends up in a
    // shared link.
    updateUrl((p) => {
      if (tenant) p.set(URL_KEYS.tenant, tenant)
      else p.delete(URL_KEYS.tenant)
      if (evt) p.set(URL_KEYS.eventType, evt)
      else p.delete(URL_KEYS.eventType)
      if (reservation) p.set(URL_KEYS.reservation, reservation)
      else p.delete(URL_KEYS.reservation)
      if (migration) p.set(URL_KEYS.migration, migration)
      else p.delete(URL_KEYS.migration)
    })
  }, [tenantId, eventType, reservationId, migrationId, updateUrl])

  const onReset = useCallback(() => {
    setTenantId("")
    setEventType("")
    setReservationId("")
    setMigrationId("")
    setLockerId("")
    setTimeRangeMs(DEFAULT_TIME_RANGE_MS)
    setEnabledStatuses(new Set(ALL_STATUSES))
    const next: GetTraceListArgs = { limit: PAGE_SIZE }
    setAppliedArgs((prev) => (sameAppliedArgs(prev, next) ? prev : next))
    // Clear only our keys — leave parent-owned params (e.g. ?tab=…) intact.
    updateUrl((p) => {
      for (const k of Object.values(URL_KEYS)) p.delete(k)
    })
  }, [updateUrl])

  const toggleStatus = useCallback((status: TraceStatus) => {
    setEnabledStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })
  }, [])

  const onLoadMore = useCallback(() => {
    setCursor(data?.data?.nextCursor)
  }, [data])

  // Metrics-section click handler — applies the chosen value as a filter.
  // Backend filters (tenant / eventType / reservation) commit immediately so
  // the user sees the filtered table without a separate Apply click. Locker
  // filters apply client-side so we just sync the input state + URL.
  const onSelectMetricFilter = useCallback(
    (target: TraceMetricFilterTarget) => {
      if (target.kind === "locker") {
        setLockerId(target.value)
        return
      }
      if (target.kind === "tenant") {
        setTenantId(target.value)
      } else if (target.kind === "eventType") {
        setEventType(target.value)
      } else if (target.kind === "reservation") {
        setReservationId(target.value)
      }
      const next: GetTraceListArgs = {
        tenantId:
          target.kind === "tenant" ? target.value : appliedArgs.tenantId,
        eventType:
          target.kind === "eventType" ? target.value : appliedArgs.eventType,
        reservationId:
          target.kind === "reservation"
            ? target.value
            : appliedArgs.reservationId,
        migrationId: appliedArgs.migrationId,
        limit: PAGE_SIZE,
      }
      setAppliedArgs((prev) => (sameAppliedArgs(prev, next) ? prev : next))
      updateUrl((p) => {
        if (next.tenantId) p.set(URL_KEYS.tenant, next.tenantId)
        else p.delete(URL_KEYS.tenant)
        if (next.eventType) p.set(URL_KEYS.eventType, next.eventType)
        else p.delete(URL_KEYS.eventType)
        if (next.reservationId)
          p.set(URL_KEYS.reservation, next.reservationId)
        else p.delete(URL_KEYS.reservation)
      })
    },
    [appliedArgs, updateUrl],
  )

  /**
   * One-click pivot from any migration trace to the whole migration: commits the
   * backend `migrationId` filter immediately (no Apply round-trip) and mirrors it
   * into the URL so the view is shareable. This is the "get all events related to
   * one sl2_locker_id" path.
   */
  const onFilterByMigration = useCallback(
    (value: string) => {
      const next: GetTraceListArgs = {
        ...appliedArgs,
        migrationId: value,
        limit: PAGE_SIZE,
      }
      setAppliedArgs((prev) => (sameAppliedArgs(prev, next) ? prev : next))
      updateUrl((p) => p.set(URL_KEYS.migration, value))
    },
    [appliedArgs, updateUrl],
  )

  const openDetail = useCallback(
    (traceId: string) => {
      navigate(`/sa/sstv2/traces/${encodeURIComponent(traceId)}`)
    },
    [navigate],
  )

  // Copy-link affordance — `window.location.href` already includes the
  // current filter query string thanks to the URL-first wiring above, so
  // copying it gives the user a shareable bookmark of exactly what they're
  // looking at. The icon swaps to a checkmark for ~1.5s on success.
  const [linkCopied, setLinkCopied] = useState(false)
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onCopyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setLinkCopied(true)
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current)
      copyResetTimerRef.current = setTimeout(() => setLinkCopied(false), 1500)
    } catch (err) {
      // Clipboard API can reject on insecure origins or denied permissions.
      // Silent fail — keep the icon in the idle state so the user can retry.
      console.warn("Copy share link failed:", err)
    }
  }, [])
  // Clear the pending timer if the component unmounts mid-feedback.
  useEffect(
    () => () => {
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current)
    },
    [],
  )

  const anyFiltersLoading =
    isFetching || tenantsQuery.isFetching || eventDiagramsQuery.isFetching

  // Number of non-default filters currently affecting the view. Drives the
  // header badge and the Reset button's disabled state.
  const activeFilterCount = useMemo(() => {
    let n = 0
    if (appliedArgs.tenantId) n += 1
    if (appliedArgs.eventType) n += 1
    if (appliedArgs.reservationId) n += 1
    if (appliedArgs.migrationId) n += 1
    if (lockerId.trim()) n += 1
    if (timeRangeMs !== DEFAULT_TIME_RANGE_MS) n += 1
    if (enabledStatuses.size !== ALL_STATUSES.length) n += 1
    return n
  }, [appliedArgs, lockerId, timeRangeMs, enabledStatuses])

  // Whether the draft (typed) backend filters differ from the applied set —
  // when false, the Apply button is disabled because there's nothing to
  // commit. Client-side filters apply on change so they don't factor in.
  const hasPendingChanges = useMemo(() => {
    return (
      (tenantId.trim() || "") !== (appliedArgs.tenantId ?? "") ||
      (eventType.trim() || "") !== (appliedArgs.eventType ?? "") ||
      (reservationId.trim() || "") !== (appliedArgs.reservationId ?? "") ||
      (migrationId.trim() || "") !== (appliedArgs.migrationId ?? "")
    )
  }, [tenantId, eventType, reservationId, migrationId, appliedArgs])

  // Common SX shared across the row labels (Time range / Status). Keeps
  // them visually aligned and easy to scan as a column when the eye drops
  // down the filter card.
  const rowLabelSx = {
    minWidth: 92,
    color: "text.secondary",
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: "uppercase" as const,
  }

  // Sentence-case button override applied to all action buttons. MUI's
  // default UPPERCASE feels old-school next to the rest of the polish.
  const actionButtonSx = { textTransform: "none" as const }

  return (
    <Box data-ui="UI-SL2W-244">
      <Paper
        variant="outlined"
        sx={{ mb: 2, position: "relative", overflow: "hidden" }}
      >
        {/* Top-edge indeterminate progress bar (Linear/Grafana pattern).
            Replaces the inline spinner for a more ambient loading state. */}
        {anyFiltersLoading && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
            }}
          />
        )}

        <Box sx={{ p: 2 }}>
          {/* Header: title, active-filter count, action buttons. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                letterSpacing: 1.2,
                color: "text.primary",
              }}
            >
              Filters
            </Typography>
            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                color="primary"
                sx={{
                  height: 20,
                  minWidth: 24,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip
              title={linkCopied ? "Link copied" : "Copy share link"}
            >
              <IconButton
                size="small"
                onClick={onCopyShareLink}
                color={linkCopied ? "success" : "default"}
              >
                {linkCopied ? (
                  <Check fontSize="small" />
                ) : (
                  <LinkIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => refetch()}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 0.75 }}
            />

            <Button
              size="small"
              onClick={onReset}
              disabled={activeFilterCount === 0}
              sx={actionButtonSx}
              color="inherit"
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={onApplyFilters}
              disabled={!hasPendingChanges}
              sx={actionButtonSx}
              disableElevation
            >
              Apply
            </Button>
          </Stack>

          {/* Time range — toggle group with inline label. One-click switching. */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mb: 1.75 }}
          >
            <Typography variant="caption" sx={rowLabelSx}>
              Time range
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={timeRangeMs}
              onChange={(_, v) => {
                if (v != null) setTimeRangeMs(v as number)
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 2,
                  py: 0.4,
                  fontSize: 12,
                  fontWeight: 500,
                  borderColor: "divider",
                },
              }}
            >
              {TIME_RANGE_OPTIONS.map((opt) => (
                <Tooltip key={opt.label} title={opt.label} disableInteractive>
                  <ToggleButton value={opt.ms} aria-label={opt.label}>
                    {opt.short}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          </Stack>

          {/* Filter inputs row */}
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="trace-tenant-label" shrink>
                Tenant
              </InputLabel>
              <Select
                labelId="trace-tenant-label"
                label="Tenant"
                displayEmpty
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value as string)}
              >
                <MenuItem value="">
                  <em>Any tenant</em>
                </MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name ?? t.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="trace-event-type-label" shrink>
                Event type
              </InputLabel>
              <Select
                labelId="trace-event-type-label"
                label="Event type"
                displayEmpty
                value={eventType}
                onChange={(e) => setEventType(e.target.value as string)}
                renderValue={(selected) =>
                  selected ? (
                    <span style={{ fontFamily: "monospace" }}>
                      {selected as string}
                    </span>
                  ) : (
                    <em>Any event type</em>
                  )
                }
              >
                <MenuItem value="">
                  <em>Any event type</em>
                </MenuItem>
                {/*
                  MUI's Select walks React.Children to compute its value
                  mapping, so subheaders + items must be flat siblings —
                  hence .flatMap().
                */}
                {eventTypeGroups.flatMap((group) => [
                  <ListSubheader
                    key={`hdr-${group.category}`}
                    sx={{
                      textTransform: "capitalize",
                      fontSize: 11,
                      lineHeight: 2,
                      letterSpacing: 0.5,
                      bgcolor: "background.paper",
                    }}
                  >
                    {group.category}
                  </ListSubheader>,
                  ...group.items.map((et) => (
                    <MenuItem key={et.eventType} value={et.eventType}>
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        spacing={1}
                        sx={{ width: "100%" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {et.eventType}
                        </Typography>
                        {et.label && et.label !== et.eventType && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: "auto" }}
                          >
                            {et.label}
                          </Typography>
                        )}
                      </Stack>
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Reservation ID"
              value={reservationId}
              onChange={(e) => setReservationId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && hasPendingChanges) {
                  e.preventDefault()
                  onApplyFilters()
                }
              }}
              sx={{ minWidth: 200 }}
              InputProps={{
                endAdornment: reservationId ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setReservationId("")}
                      aria-label="Clear reservation ID"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            <TextField
              size="small"
              label="Migration ID"
              value={migrationId}
              onChange={(e) => setMigrationId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && hasPendingChanges) {
                  e.preventDefault()
                  onApplyFilters()
                }
              }}
              helperText="Every event of one V1→V2 migration"
              sx={{ minWidth: 260 }}
              InputProps={{
                endAdornment: migrationId ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setMigrationId("")}
                      aria-label="Clear migration ID"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            <TextField
              size="small"
              label="Locker ID"
              value={lockerId}
              onChange={(e) => setLockerId(e.target.value)}
              sx={{ minWidth: 200 }}
              InputProps={{
                endAdornment: lockerId ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setLockerId("")}
                      aria-label="Clear locker ID"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Stack>

          <Divider sx={{ my: 1.75 }} />

          {/* Status chips */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="caption" sx={rowLabelSx}>
              Status
            </Typography>
            {ALL_STATUSES.map((s) => (
              <Chip
                key={s}
                label={s}
                color={getMuiTraceStatusColor(s)}
                variant={enabledStatuses.has(s) ? "filled" : "outlined"}
                onClick={() => toggleStatus(s)}
                size="small"
                sx={{ textTransform: "none" }}
              />
            ))}
            <Box sx={{ flexGrow: 1 }} />
            {anyInProgress && (
              <Chip
                label="Live updates on"
                size="small"
                variant="outlined"
                color="info"
                sx={{
                  textTransform: "none",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            )}
          </Stack>
        </Box>
      </Paper>

      <Sstv2TraceMetricsSection
        onSelectFilter={onSelectMetricFilter}
        tenantNameById={tenantNameById}
      />

      {/* Table */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Started</TableCell>
            <TableCell>Event Type</TableCell>
            <TableCell>Tenant</TableCell>
            <TableCell>Reservation</TableCell>
            <TableCell>Locker</TableCell>
            <TableCell>Migration</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Steps / Errors</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.length === 0 && !isFetching ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography variant="caption" color="text.secondary">
                  No traces found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            visible.map((t) => (
              <TableRow
                key={t.traceId}
                hover
                onClick={() => openDetail(t.traceId)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Tooltip title={fmtAbsolute(t.startedAt)}>
                    <Typography variant="caption" fontFamily="monospace">
                      {fmtRelative(t.startedAt)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      size="small"
                      label={t.eventType}
                      variant="outlined"
                      color="primary"
                    />
                    {t.approximateReceiveCount != null &&
                      t.approximateReceiveCount > 1 && (
                        <Tooltip
                          title={`Redelivered by SQS — receipt #${t.approximateReceiveCount} (retried after a prior receipt)`}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            icon={<Refresh fontSize="small" />}
                            label={`#${t.approximateReceiveCount}`}
                          />
                        </Tooltip>
                      )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {t.tenantId ? (
                    <Tooltip title={t.tenantId}>
                      <span>
                        {tenantNameById.get(t.tenantId) ?? t.tenantId}
                      </span>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <SstSl2IdPair
                    kind="reservation"
                    sstId={t.sstReservationId}
                    sl2Id={t.reservationId}
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <SstSl2IdPair
                    kind="locker"
                    sstId={t.sstLockerId}
                    sl2Id={t.lockerId}
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {t.migrationId ? (
                    <Tooltip title={`Filter to every event of migration ${t.migrationId}`}>
                      <Chip
                        size="small"
                        label={t.migrationId.slice(0, 8)}
                        onClick={() => {
                          setMigrationId(t.migrationId as string)
                          onFilterByMigration(t.migrationId as string)
                        }}
                        // FILLED brand blue, white text (~9.6:1 on white). The
                        // previous outlined `secondary` chip was theme mint
                        // (#94D2C2) on white — ~1.7:1, unreadable and well under
                        // the WCAG AA 4.5:1 floor. Filled also separates this
                        // chip from its neighbours by SHAPE, not just hue:
                        // eventType is outlined-primary and redelivery is
                        // outlined-warning, so a solid fill reads as an
                        // identity/pivot control rather than another status.
                        sx={{
                          fontFamily: "monospace",
                          cursor: "pointer",
                          bgcolor: "primary.main",
                          color: "common.white",
                          fontWeight: 600,
                          "&:hover": { bgcolor: "primary.dark" },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <StatusChip status={t.status} />
                </TableCell>
                <TableCell>{fmtDuration(t.durationMs)}</TableCell>
                <TableCell>{`${t.stepCount} / ${t.errorCount}`}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/*
        Show "Load more" only when the backend reports more pages AND the
        latest page actually returned items. The Scan-based fallback can emit
        a cursor pointing past end-of-data when the scan budget exhausts on
        filtered-out items, so a non-null cursor alone isn't enough — empty
        items + cursor signals we're past meaningful data.
      */}
      {!!data?.data?.nextCursor && (data.data.items?.length ?? 0) > 0 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Button onClick={onLoadMore} disabled={isFetching}>
            Load more
          </Button>
        </Stack>
      )}
    </Box>
  )
}

export default Sstv2TracesTab
